import React, { useEffect, useState } from 'react';
import { View, Text, Image, ImageBackground, Pressable, StyleSheet } from 'react-native';
import type { SectionKey } from './proGroupsNavigationConfig';
import { resolveVisibleSections } from './proGroupsNavigationConfig';
import { getProGroupsLayoutMaxWidth } from '../../config/configManager';
import { Base64ImageStorage, extractImgSrc } from '../../storage/Base64ImageStorage';

// Previous auto height (~80px: 44px logo + 18px*2 vertical padding), made 50px taller to
// give the enlarged 120px logo room to breathe. Background images are sized to match exactly.
const HEADER_ROW_HEIGHT = 90;

// Every skin's logo scales to this common height, with width computed from its own aspect
// ratio (via Image.getSize) so differently-shaped logos aren't all squeezed to one width.
const LOGO_HEIGHT = 65;

// Fixed Base64Image contenttype ids for each nav section's icon — not skin-dependent, unlike
// logo/background, so these are resolved independently of resolveSkinChannelInfo.
const SECTION_ICON_IMAGE_ID: Partial<Record<SectionKey, string>> = {
  Clients: '102',
  Stats: '103',
  Account: '104',
};

const resolveRuntimeStorage = (): any => {
  return (globalThis as any).__proGroupsStorage ?? (globalThis as any).__childNavStorage ?? null;
};

// Module-level cache + in-flight-promise dedup — same pattern as ProfileRowEditor.tsx's
// PDF/expand-collapse icon loading, since these ids never change across renders/mounts.
let cachedSectionIcons: Partial<Record<SectionKey, string>> | null = null;
let sectionIconsLoadPromise: Promise<Partial<Record<SectionKey, string>>> | null = null;

const loadSectionIcons = (): Promise<Partial<Record<SectionKey, string>>> => {
  if (cachedSectionIcons) {
    return Promise.resolve(cachedSectionIcons);
  }
  if (sectionIconsLoadPromise) {
    return sectionIconsLoadPromise;
  }

  sectionIconsLoadPromise = (async () => {
    const storage = resolveRuntimeStorage();
    if (!storage) {
      cachedSectionIcons = {};
      return cachedSectionIcons;
    }

    const imageStorage = new Base64ImageStorage(storage);
    const entries = await Promise.all(
      (Object.entries(SECTION_ICON_IMAGE_ID) as Array<[SectionKey, string]>).map(async ([section, imageId]) => {
        const image = await imageStorage.getImage(imageId);
        const src = (image?.base64 && extractImgSrc(image.base64)) || null;
        return [section, src] as const;
      })
    );

    cachedSectionIcons = Object.fromEntries(
      entries.filter((entry): entry is [SectionKey, string] => !!entry[1])
    );
    return cachedSectionIcons;
  })();

  return sectionIconsLoadPromise;
};

interface ProGroupsMainMenuProps {
  activeSection: SectionKey;
  onSectionChange: (section: SectionKey) => void;
  onSignOut: () => void;
  showDebugTab: boolean;
  visibleSections?: SectionKey[];
  /** SkinTypeDef ChannelInfo settings for the active account (see resolveSkinChannelInfo) — falls back to the defaults below when unresolved/undefined. */
  logoSrc?: string | null;
  backgroundSrc?: string | null;
  menuFontColor?: string | null;
  skinBgColor?: string | null;
}

export const ProGroupsMainMenu: React.FC<ProGroupsMainMenuProps> = ({
  activeSection,
  onSectionChange,
  onSignOut,
  showDebugTab,
  visibleSections,
  logoSrc,
  backgroundSrc,
  menuFontColor,
  skinBgColor,
}) => {
  const resolvedVisibleSections = visibleSections ?? resolveVisibleSections(showDebugTab);
  const maxWidth = getProGroupsLayoutMaxWidth();
  const [sectionIcons, setSectionIcons] = useState<Partial<Record<SectionKey, string>>>(cachedSectionIcons ?? {});
  const [logoWidth, setLogoWidth] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    void loadSectionIcons().then((icons) => {
      if (!cancelled) {
        setSectionIcons(icons);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // RN Image needs an explicit numeric width — 'auto' collapses to 0 on react-native-web's
  // background-image-based rendering (no intrinsic size to derive width from). Measure the
  // real logo dimensions instead and scale width to match the fixed LOGO_HEIGHT ourselves.
  useEffect(() => {
    if (!logoSrc) {
      setLogoWidth(null);
      return;
    }
    let cancelled = false;
    Image.getSize(
      logoSrc,
      (naturalWidth, naturalHeight) => {
        if (!cancelled && naturalHeight > 0) {
          setLogoWidth((naturalWidth / naturalHeight) * LOGO_HEIGHT);
        }
      },
      (error) => {
        console.warn('ProGroupsMainMenu: failed to measure logo image size', error);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [logoSrc]);

  const headerContent = (
    <View style={[styles.header, { maxWidth }]}>
      <View style={styles.brandBlock}>
        <View
          style={[
            styles.logoBadge,
            logoSrc ? [styles.logoBadgeWithImage, logoWidth ? { width: logoWidth } : null] : null,
          ]}
        >
          {logoSrc ? (
            <Image
              source={{ uri: logoSrc }}
              style={[styles.logoImage, logoWidth ? { width: logoWidth } : null]}
              resizeMode="contain"
            />
          ) : (
            <Text style={styles.logoText}>PG</Text>
          )}
        </View>
      </View>

      <View style={styles.topMenu}>
        {resolvedVisibleSections.map((section) => {
          const active = activeSection === section;
          const iconSrc = sectionIcons[section];
          return (
            <Pressable
              key={section}
              onPress={() => onSectionChange(section)}
              style={[
                styles.topMenuItem,
                active ? { borderBottomColor: menuFontColor || styles.topMenuLabel.color } : null,
              ]}
              accessibilityRole="button"
              accessibilityLabel={`Switch to ${section}`}
            >
              {iconSrc && (
                <Image
                  source={{ uri: iconSrc }}
                  style={[styles.sectionIcon, { tintColor: menuFontColor || styles.topMenuLabel.color }]}
                  resizeMode="contain"
                />
              )}
              <Text style={[styles.topMenuLabel, menuFontColor ? { color: menuFontColor } : null]}>
                {section}
              </Text>
            </Pressable>
          );
        })}

        <Pressable
          onPress={onSignOut}
          style={styles.topMenuItem}
          accessibilityRole="button"
          accessibilityLabel="Sign out"
        >
          <Text style={[styles.topMenuLabel, menuFontColor ? { color: menuFontColor } : null]}>Sign Out</Text>
        </Pressable>
      </View>
    </View>
  );

  // The original system paints the header as a table with background-size:100% 100%
  // (a non-uniform stretch, not crop-to-fill) and no background-color behind it — the source
  // PNG has a transparent arc baked into its bottom edge that reveals the page's own background
  // through the header. So skinBgColor is only a fallback fill for when there's no background
  // image at all; the moment one resolves, the shell must stay transparent or that reveal is lost.
  return (
    <View
      style={[
        styles.headerShell,
        backgroundSrc
          ? { backgroundColor: 'transparent' }
          : skinBgColor
          ? { backgroundColor: skinBgColor }
          : null,
      ]}
    >
      {backgroundSrc ? (
        <ImageBackground source={{ uri: backgroundSrc }} style={styles.headerBackgroundImage} resizeMode="stretch">
          {headerContent}
        </ImageBackground>
      ) : (
        headerContent
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  headerShell: {
    width: '100%',
    backgroundColor: '#0f172a',
  },
  headerBackgroundImage: {
    width: '100%',
    height: HEADER_ROW_HEIGHT,
  },
  header: {
    width: '100%',
    height: HEADER_ROW_HEIGHT,
    alignSelf: 'center',
    paddingHorizontal: 24,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.08)',
  },
  brandBlock: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  logoBadge: {
    width: 44,
    height: 44,
    borderRadius: 14,
    backgroundColor: '#1f6feb',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 14,
  },
  logoBadgeWithImage: {
    height: LOGO_HEIGHT,
    width: LOGO_HEIGHT,
    backgroundColor: 'transparent',
  },
  logoImage: {
    height: LOGO_HEIGHT,
    width: LOGO_HEIGHT,
  },
  logoText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '800',
    letterSpacing: 0.8,
  },
  topMenu: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  topMenuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 5,
    marginLeft: 10,
    backgroundColor: 'transparent',
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  sectionIcon: {
    width: 20,
    height: 20,
    marginRight: 8,
  },
  topMenuLabel: {
    color: '#dbe7ff',
    fontSize: 15,
    fontFamily: 'Arial',
    fontWeight: 'normal',
  },
});
